import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Sparkles, Search, X, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Input';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { GEO_COUNTRIES } from '@/data/geo-locations';
import { WORLD_INDUSTRIES } from '@/data/world-industries';
import { WORLD_DEPTS_BY_REGION, WORLD_REGIONS_BY_CC } from '@/data/world-locations';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────

export interface LeadScrapeLocation {
  countryCode: string;
  country: string;
  kind: 'city' | 'department' | 'region';
  name: string;
  code?: string;
  fallbackRank?: number;
}

export interface LeadScrapeSelectionFlags {
  emails_and_contacts: boolean;
  verify_emails: boolean;
  reviews_and_ratings: boolean;
  advertising_tech_stack: boolean;
  seo_data: boolean;
}

export interface LeadScrapeTargeting {
  industries: string[];
  locations: LeadScrapeLocation[];
  selectionFlags: LeadScrapeSelectionFlags;
}

interface CampaignContext {
  sender_company?: string | null;
  sender_description?: string | null;
  sender_website?: string | null;
  offer_title?: string | null;
  offer_description?: string | null;
  offer_url?: string | null;
  target?: string | null;
  target_region?: string | null;
  /** DataForSEO language_code — see /v1/dataforseo/languages. */
  language?: string | null;
}

interface Props {
  value: LeadScrapeTargeting;
  onChange: (next: LeadScrapeTargeting) => void;
  campaignContext?: CampaignContext;
}

// ── Static helpers ───────────────────────────────────────────────────

const LS_COUNTRIES = Object.keys(WORLD_REGIONS_BY_CC)
  .map((cc) => ({ cc, name: GEO_COUNTRIES.find((c) => c.iso === cc)?.name ?? cc }))
  .sort((a, b) => a.name.localeCompare(b.name));

const IND_PRESETS: { label: string; values: string[] }[] = [
  { label: '🍽️ Restauration', values: ['Restaurant', 'Fast food restaurant', 'Cafe', 'Bakery', 'Pizza restaurant', 'Bar', 'French restaurant', 'Brasserie', 'Catering food and drink supplier'] },
  { label: '🔧 Artisanat / BTP', values: ['Plumber', 'Electrician', 'Painter', 'Roofer', 'Carpenter', 'General contractor', 'Heating contractor', 'Tiler', 'Mason'] },
  { label: '💇 Beauté', values: ['Hair salon', 'Beauty salon', 'Nail salon', 'Barber shop', 'Spa', 'Hairdresser', 'Waxing hair removal service'] },
  { label: '⚕️ Santé', values: ['Doctor', 'Dentist', 'Pharmacy', 'Physiotherapist', 'Optician', 'Ophthalmologist', 'General practitioner'] },
  { label: '🚗 Automobile', values: ['Car dealer', 'Car repair shop', 'Auto parts store', 'Car wash', 'Tire shop', 'Used car dealer', 'Car detailing service'] },
  { label: '🏠 Immobilier', values: ['Real estate agency', 'Real estate agent', 'Property management company', 'Real estate rental agency'] },
];

const LOC_PRESETS: { label: string; cc: string; kind: 'region' | 'department'; regionCode?: string }[] = [
  { label: '🇫🇷 Tous les depts France', cc: 'FR', kind: 'department' },
  { label: '🗼 Île-de-France', cc: 'FR', kind: 'department', regionCode: 'FR.11' },
  { label: '⛰️ Auvergne-Rhône-Alpes', cc: 'FR', kind: 'department', regionCode: 'FR.84' },
  { label: '☀️ PACA', cc: 'FR', kind: 'department', regionCode: 'FR.93' },
  { label: '🌾 Occitanie', cc: 'FR', kind: 'department', regionCode: 'FR.76' },
  { label: '🏭 Hauts-de-France', cc: 'FR', kind: 'department', regionCode: 'FR.32' },
];

const SELECTION_FLAG_LABELS: Record<keyof LeadScrapeSelectionFlags, string> = {
  emails_and_contacts: 'Emails & contacts',
  verify_emails: 'Vérification des emails',
  reviews_and_ratings: 'Avis & notes',
  advertising_tech_stack: 'Stack pub & techno',
  seo_data: 'Données SEO',
};

// ── City data loader (lazy, cached) ──────────────────────────────────

type CityEntry = [string, string]; // [name, region_code]

const cityCache: Record<string, CityEntry[]> = {};

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function resolveRegionName(countryCode: string, regionCode?: string | null): string | null {
  if (!regionCode) return null;
  const regions = WORLD_REGIONS_BY_CC[countryCode] ?? [];
  const expanded = regionCode.includes('.') ? regionCode : `${countryCode}.${regionCode}`;
  const match = regions.find((region) => region.code === expanded || region.suffix === regionCode);
  return match?.name ?? null;
}

function computeCityFallbackRank(
  countryCode: string,
  cityName: string,
  regionCode: string | null | undefined,
  allCities: readonly CityEntry[],
): number | undefined {
  if (!regionCode) return undefined;
  const normalizedName = normalizeSearchText(cityName);
  const duplicates = allCities.filter(([name]) => normalizeSearchText(name) === normalizedName);
  if (duplicates.length <= 1) return undefined;

  const ranked = [...duplicates].sort((a, b) => {
    const regionA = resolveRegionName(countryCode, a[1]) ?? a[1] ?? '';
    const regionB = resolveRegionName(countryCode, b[1]) ?? b[1] ?? '';
    const regionCompare = regionA.localeCompare(regionB, 'fr', { sensitivity: 'base' });
    if (regionCompare !== 0) return regionCompare;
    return a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' });
  });

  const index = ranked.findIndex(
    ([name, code]) => normalizeSearchText(name) === normalizedName && code === regionCode,
  );
  return index >= 0 ? index + 1 : undefined;
}

function buildCityLocation(args: {
  countryCode: string;
  countryName: string;
  entry: CityEntry;
  allCities: readonly CityEntry[];
}): LeadScrapeLocation {
  const [name, regionCode] = args.entry;
  return {
    countryCode: args.countryCode,
    country: args.countryName,
    kind: 'city',
    name,
    code: regionCode || undefined,
    fallbackRank: computeCityFallbackRank(args.countryCode, name, regionCode, args.allCities),
  };
}

function formatLocationLabel(loc: LeadScrapeLocation): string {
  if (loc.kind !== 'city') return loc.name;
  const regionName = resolveRegionName(loc.countryCode, loc.code);
  return regionName ? `${loc.name} (${regionName})` : loc.name;
}

async function loadCities(cc: string): Promise<CityEntry[]> {
  const countryCode = cc.toUpperCase();
  if (cityCache[countryCode]) return cityCache[countryCode];
  try {
    const resp = await fetch(`/data/cities/${countryCode}.json`);
    if (!resp.ok) return [];
    const data = (await resp.json()) as CityEntry[];
    cityCache[countryCode] = data;
    return data;
  } catch {
    return [];
  }
}

function useCitySearch(countryCode: string) {
  const [query, setQuery] = useState('');
  const [allCities, setAllCities] = useState<CityEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadCities(countryCode).then((data) => {
      if (!cancelled) {
        setAllCities(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const results = useMemo(() => {
    const q = normalizeSearchText(query);
    if (q.length < 2 || allCities.length === 0) return [];
    return [...allCities]
      .filter(([name]) => normalizeSearchText(name).includes(q))
      .sort((a, b) => {
        const aName = normalizeSearchText(a[0]);
        const bName = normalizeSearchText(b[0]);
        const aStarts = aName.startsWith(q) ? 0 : 1;
        const bStarts = bName.startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        const nameCompare = a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' });
        if (nameCompare !== 0) return nameCompare;
        return (a[1] ?? '').localeCompare(b[1] ?? '', 'fr', { sensitivity: 'base' });
      })
      .slice(0, 20);
  }, [query, allCities]);

  return {
    query,
    search: useCallback((q: string) => setQuery(q), []),
    clear: useCallback(() => setQuery(''), []),
    results,
    loading: loading && query.length >= 2,
    total: allCities.length,
  };
}

// ── Component ────────────────────────────────────────────────────────

export function LeadScrapeTargetingPanel({ value, onChange, campaignContext }: Props) {
  // Industry state
  const [indSearch, setIndSearch] = useState('');
  const indResults = useMemo(() => {
    const q = indSearch.trim().toLowerCase();
    return q ? WORLD_INDUSTRIES.filter((i) => i.toLowerCase().includes(q)).slice(0, 80) : [];
  }, [indSearch]);

  // AI suggest state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<string[] | null>(null);
  const [aiUnmatched, setAiUnmatched] = useState<string[]>([]);
  const [aiReasoning, setAiReasoning] = useState<string>('');
  const [aiExcludedFromHistory, setAiExcludedFromHistory] = useState<number>(0);
  const [aiChecked, setAiChecked] = useState<Set<string>>(new Set());

  // Location state
  const [locCC, setLocCC] = useState('FR');
  const [locLevel, setLocLevel] = useState<'region' | 'department' | 'city'>('department');
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [countrySearch, setCountrySearch] = useState(() => LS_COUNTRIES.find((c) => c.cc === 'FR')?.name ?? 'France');
  const [countryOpen, setCountryOpen] = useState(false);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const [locSearch, setLocSearch] = useState('');

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    return (q ? LS_COUNTRIES.filter((c) => c.name.toLowerCase().includes(q)) : LS_COUNTRIES).slice(0, 60);
  }, [countrySearch]);

  const locCountryName = LS_COUNTRIES.find((c) => c.cc === locCC)?.name ?? locCC;
  const countryRegions = WORLD_REGIONS_BY_CC[locCC] ?? [];

  const filteredRegions = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    if (!q) return countryRegions;
    return countryRegions.filter((r) => r.name.toLowerCase().includes(q));
  }, [countryRegions, locSearch]);

  const filteredRegionsForDepts = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    if (!q) return countryRegions;
    return countryRegions.filter((r) => {
      if (r.name.toLowerCase().includes(q)) return true;
      const depts = WORLD_DEPTS_BY_REGION[r.code] ?? [];
      return depts.some((d) => d.name.toLowerCase().includes(q));
    });
  }, [countryRegions, locSearch]);

  const selectedCities = value.locations.filter((l) => l.kind === 'city');
  const selectedInCC = value.locations.filter((l) => l.countryCode === locCC);
  const citySearch = useCitySearch(locCC);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  // ── Mutators ──────────────────────────────────────────────────────

  function toggleInd(ind: string) {
    const next = value.industries.includes(ind)
      ? value.industries.filter((i) => i !== ind)
      : [...value.industries, ind];
    onChange({ ...value, industries: next });
  }
  function removeInd(ind: string) {
    onChange({ ...value, industries: value.industries.filter((i) => i !== ind) });
  }
  function applyIndPreset(values: string[]) {
    const s = new Set(value.industries);
    values.forEach((v) => s.add(v));
    onChange({ ...value, industries: Array.from(s) });
  }

  function selectCountry(cc: string, name: string) {
    setLocCC(cc);
    setCountrySearch(name);
    setCountryOpen(false);
    setExpandedRegions(new Set());
  }

  function toggleExpand(regionCode: string) {
    setExpandedRegions((prev) => {
      const s = new Set(prev);
      if (s.has(regionCode)) s.delete(regionCode);
      else s.add(regionCode);
      return s;
    });
  }

  function addLocation(loc: LeadScrapeLocation) {
    const already = value.locations.some((l) => locKey(l) === locKey(loc));
    if (already) return;
    onChange({ ...value, locations: [...value.locations, loc] });
  }

  function toggleRegion(region: { code: string; name: string }) {
    const exists = value.locations.some(
      (l) => l.countryCode === locCC && l.kind === 'region' && l.code === region.code,
    );
    if (exists) {
      onChange({
        ...value,
        locations: value.locations.filter(
          (l) => !(l.countryCode === locCC && l.kind === 'region' && l.code === region.code),
        ),
      });
    } else {
      addLocation({ countryCode: locCC, country: locCountryName, kind: 'region', name: region.name, code: region.code });
    }
  }

  function toggleDept(dept: { code: string; name: string }) {
    const exists = value.locations.some(
      (l) => l.countryCode === locCC && l.kind === 'department' && l.code === dept.code,
    );
    if (exists) {
      onChange({
        ...value,
        locations: value.locations.filter(
          (l) => !(l.countryCode === locCC && l.kind === 'department' && l.code === dept.code),
        ),
      });
    } else {
      addLocation({ countryCode: locCC, country: locCountryName, kind: 'department', name: dept.name, code: dept.code });
    }
  }

  function addAllForCountry(cc: string, countryName: string, kind: 'region' | 'department') {
    const regions = WORLD_REGIONS_BY_CC[cc] ?? [];
    const toAdd: LeadScrapeLocation[] = [];
    if (kind === 'region') {
      for (const r of regions) {
        if (!value.locations.some((l) => l.code === r.code)) {
          toAdd.push({ countryCode: cc, country: countryName, kind: 'region', name: r.name, code: r.code });
        }
      }
    } else {
      for (const r of regions) {
        const depts = WORLD_DEPTS_BY_REGION[r.code] ?? [];
        for (const d of depts) {
          if (!value.locations.some((l) => l.code === d.code)) {
            toAdd.push({ countryCode: cc, country: countryName, kind: 'department', name: d.name, code: d.code });
          }
        }
      }
    }
    onChange({ ...value, locations: [...value.locations, ...toAdd] });
  }

  function addAllForRegion(regionCode: string) {
    const depts = WORLD_DEPTS_BY_REGION[regionCode] ?? [];
    const toAdd: LeadScrapeLocation[] = [];
    for (const d of depts) {
      if (!value.locations.some((l) => l.code === d.code)) {
        toAdd.push({ countryCode: locCC, country: locCountryName, kind: 'department', name: d.name, code: d.code });
      }
    }
    onChange({ ...value, locations: [...value.locations, ...toAdd] });
  }

  function clearAllForRegion(regionCode: string) {
    const deptCodes = new Set((WORLD_DEPTS_BY_REGION[regionCode] ?? []).map((d) => d.code));
    onChange({ ...value, locations: value.locations.filter((l) => !deptCodes.has(l.code ?? '')) });
  }

  function applyLocPreset(preset: (typeof LOC_PRESETS)[number]) {
    const name = LS_COUNTRIES.find((c) => c.cc === preset.cc)?.name ?? preset.cc;
    if (preset.regionCode) {
      setLocCC(preset.cc);
      setCountrySearch(name);
      addAllForRegion(preset.regionCode);
    } else {
      addAllForCountry(preset.cc, name, preset.kind);
    }
  }

  function removeLoc(idx: number) {
    onChange({ ...value, locations: value.locations.filter((_, i) => i !== idx) });
  }

  function clearAll() {
    onChange({ ...value, industries: [], locations: [] });
  }

  function setFlag(key: keyof LeadScrapeSelectionFlags, v: boolean) {
    onChange({ ...value, selectionFlags: { ...value.selectionFlags, [key]: v } });
  }

  // ── AI suggest ───────────────────────────────────────────────────

  async function runAISuggest(regenerate: boolean) {
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    setAiUnmatched([]);
    setAiExcludedFromHistory(0);
    try {
      const body: Record<string, unknown> = {
        senderCompanyName: campaignContext?.sender_company,
        senderDescription: campaignContext?.sender_description,
        senderWebsite: campaignContext?.sender_website,
        offerTitle: campaignContext?.offer_title,
        offerDescription: campaignContext?.offer_description,
        offerUrl: campaignContext?.offer_url,
        targetCountry: campaignContext?.target,
        targetRegion: campaignContext?.target_region,
        language: campaignContext?.language ?? 'fr',
      };
      if (regenerate && value.industries.length > 0) {
        body.existingIndustries = value.industries;
      }
      const { data: json } = await apiClient.post<{
        industries?: string[];
        matched?: string[];
        unmatched?: string[];
        reasoning?: string;
        meta?: { excludedFromHistory?: number };
      }>('/v1/llm/suggest-leadscrape-targeting', body);
      if (!json || !Array.isArray(json.industries)) {
        setAiError("Réponse IA invalide.");
        return;
      }
      setAiSuggestions(json.industries);
      setAiUnmatched(json.unmatched ?? []);
      setAiReasoning(json.reasoning ?? '');
      setAiExcludedFromHistory(json.meta?.excludedFromHistory ?? 0);
      setAiChecked(new Set(json.industries));
    } catch (err) {
      setAiError(
        getApiErrorMessage(err, {
          ai_not_configured: "L'IA n'est pas configurée.",
          llm_upstream_failed: 'Le LLM est indisponible — réessaye dans quelques secondes.',
          ERR_VALIDATION: 'Champs invalides — vérifie le contexte de la campagne.',
        }),
      );
    } finally {
      setAiLoading(false);
    }
  }

  function validateAISelection() {
    if (!aiSuggestions) return;
    const toAdd = aiSuggestions.filter((s) => aiChecked.has(s));
    const merged = new Set([...value.industries, ...toAdd]);
    onChange({ ...value, industries: Array.from(merged) });
    setAiSuggestions(null);
    setAiUnmatched([]);
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="space-y-4" data-testid="leadscrape-targeting-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm flex items-center gap-2 text-gray-800">
          <Sparkles className="h-4 w-4 text-brand-primary" strokeWidth={1.5} />
          Ciblage LeadScrape
        </h3>
        {(value.industries.length > 0 || value.locations.length > 0) && (
          <button onClick={clearAll} className="text-xs text-red-500 hover:underline">
            ✕ Tout effacer
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ── LEFT: Industries ── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-800">Industries</span>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">
                {value.industries.length} sélectionnée{value.industries.length > 1 ? 's' : ''}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => runAISuggest(value.industries.length > 0)}
                disabled={aiLoading}
              >
                {aiLoading ? (
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1" strokeWidth={1.5} />
                )}
                {value.industries.length > 0 ? 'Régénérer IA' : 'Suggestions IA'}
              </Button>
            </div>
          </div>

          {aiError && (
            <div className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.5} />
              <span>{aiError}</span>
            </div>
          )}

          {aiSuggestions && (
            <div className="border border-brand-primary/30 rounded-lg bg-brand-primary/5 p-2 space-y-2">
              <div className="text-xs text-brand-primary font-bold">✨ Suggestions IA</div>
              {aiReasoning && <p className="text-xs text-gray-500 italic">{aiReasoning}</p>}
              {aiExcludedFromHistory > 0 && (
                <p className="text-[11px] text-green-700 bg-green-50 px-1.5 py-1 rounded">
                  ↻ {aiExcludedFromHistory} industrie{aiExcludedFromHistory > 1 ? 's' : ''} déjà couverte{aiExcludedFromHistory > 1 ? 's' : ''} exclue{aiExcludedFromHistory > 1 ? 's' : ''}.
                </p>
              )}
              {aiUnmatched.length > 0 && (
                <p className="text-[11px] text-yellow-600 bg-yellow-50 px-1.5 py-1 rounded">
                  ⚠ {aiUnmatched.length} suggestion{aiUnmatched.length > 1 ? 's' : ''} hors catalogue : {aiUnmatched.join(', ')}
                </p>
              )}
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                {aiSuggestions.map((s) => {
                  const isUnmatched = aiUnmatched.includes(s);
                  return (
                    <label
                      key={s}
                      className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 border rounded text-xs cursor-pointer',
                        aiChecked.has(s)
                          ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                          : 'bg-white border-brand-border text-gray-500',
                        isUnmatched && 'border-yellow-400',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="w-3 h-3 accent-brand-primary"
                        checked={aiChecked.has(s)}
                        onChange={() =>
                          setAiChecked((prev) => {
                            const n = new Set(prev);
                            if (n.has(s)) n.delete(s);
                            else n.add(s);
                            return n;
                          })
                        }
                      />
                      {s}
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={validateAISelection}>
                  <Check className="h-3 w-3 mr-1" strokeWidth={1.5} />
                  Valider ({aiChecked.size})
                </Button>
                <button
                  onClick={() => { setAiSuggestions(null); setAiUnmatched([]); }}
                  className="text-xs text-gray-500 hover:text-gray-800 ml-auto"
                >
                  annuler
                </button>
              </div>
            </div>
          )}

          {/* Industry presets */}
          <div className="flex flex-wrap gap-1">
            {IND_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyIndPreset(p.values)}
                className="text-xs px-1.5 py-0.5 border border-brand-border rounded hover:bg-brand-primary/5 hover:border-brand-primary/30 text-gray-500 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 pointer-events-none" strokeWidth={1.5} />
            <Input
              placeholder={`Rechercher dans ${WORLD_INDUSTRIES.length.toLocaleString('fr')} catégories GBP...`}
              value={indSearch}
              onChange={(e) => setIndSearch(e.target.value)}
              className="pl-8 text-xs"
              data-testid="industry-search"
            />
          </div>

          {/* Catalog results */}
          {indResults.length > 0 && (
            <div className="border border-brand-border rounded-lg overflow-y-auto max-h-40 bg-white" data-testid="industry-results">
              {indResults.map((ind) => {
                const checked = value.industries.includes(ind);
                return (
                  <label
                    key={ind}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-gray-50',
                      checked && 'bg-brand-primary/5',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleInd(ind)}
                      className="w-3 h-3 accent-brand-primary shrink-0"
                    />
                    <span className="truncate">{ind}</span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Selected chips */}
          {value.industries.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto p-1.5 bg-gray-50 rounded-lg border border-brand-border" data-testid="selected-industries">
              {value.industries.map((ind) => (
                <span
                  key={ind}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-brand-border rounded text-xs text-gray-800"
                >
                  {ind}
                  <button
                    onClick={() => removeInd(ind)}
                    className="text-gray-400 hover:text-red-500"
                    aria-label={`Retirer ${ind}`}
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── RIGHT: Locations ── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-800">Localisations</span>
            <span className="text-[11px] text-gray-500">
              {value.locations.length} sélectionnée{value.locations.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Location presets */}
          <div className="flex flex-wrap gap-1">
            {LOC_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyLocPreset(p)}
                className="text-xs px-1.5 py-0.5 border border-brand-border rounded hover:bg-brand-primary/5 hover:border-brand-primary/30 text-gray-500 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Country picker */}
          <div className="relative">
            <Input
              ref={countryInputRef}
              placeholder="Pays..."
              value={countrySearch}
              onChange={(e) => { setCountrySearch(e.target.value); setCountryOpen(true); }}
              onFocus={() => setCountryOpen(true)}
              onBlur={() => setTimeout(() => setCountryOpen(false), 150)}
              className="text-xs"
            />
            {countryOpen && filteredCountries.length > 0 && (
              <ul className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-brand-border rounded-lg shadow-lg max-h-44 overflow-y-auto text-xs">
                {filteredCountries.map((c) => (
                  <li
                    key={c.cc}
                    className={cn(
                      'px-3 py-1.5 cursor-pointer hover:bg-gray-50',
                      c.cc === locCC && 'font-semibold text-brand-primary',
                    )}
                    onMouseDown={(e) => { e.preventDefault(); selectCountry(c.cc, c.name); }}
                  >
                    {c.name}{' '}
                    <span className="text-gray-400 font-mono text-[10px]">{c.cc}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Level tabs */}
          <div className="flex gap-1">
            {(['region', 'department', 'city'] as const).map((lv) => (
              <button
                key={lv}
                onClick={() => { setLocLevel(lv); setLocSearch(''); }}
                className={cn(
                  'text-xs px-2 py-1 rounded border transition-colors',
                  locLevel === lv
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'border-brand-border text-gray-500 hover:bg-gray-50',
                )}
              >
                {lv === 'region' ? 'Régions' : lv === 'department' ? 'Départements' : 'Villes'}
              </button>
            ))}
          </div>

          {/* Search filter for regions/depts */}
          {locLevel !== 'city' && (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 pointer-events-none" strokeWidth={1.5} />
              <Input
                placeholder={locLevel === 'region' ? 'Filtrer les régions...' : 'Filtrer régions ou départements...'}
                value={locSearch}
                onChange={(e) => setLocSearch(e.target.value)}
                className="pl-8 text-xs"
              />
            </div>
          )}

          {/* City picker */}
          {locLevel === 'city' && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 pointer-events-none" strokeWidth={1.5} />
                <Input
                  placeholder={
                    citySearch.total > 0
                      ? `Rechercher parmi ${citySearch.total.toLocaleString('fr')} villes...`
                      : 'Chargement des villes...'
                  }
                  value={citySearch.query}
                  onChange={(e) => { citySearch.search(e.target.value); setCityDropdownOpen(true); }}
                  onFocus={() => citySearch.results.length > 0 && setCityDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setCityDropdownOpen(false), 200)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && citySearch.query.trim()) {
                      const firstMatch = citySearch.results[0];
                      const nextLocation = firstMatch
                        ? buildCityLocation({
                            countryCode: locCC,
                            countryName: locCountryName,
                            entry: firstMatch,
                            allCities: cityCache[locCC] ?? [],
                          })
                        : {
                            countryCode: locCC,
                            country: locCountryName,
                            kind: 'city' as const,
                            name: citySearch.query.trim(),
                          };
                      addLocation(nextLocation);
                      citySearch.clear();
                      setCityDropdownOpen(false);
                    }
                  }}
                  className="pl-8 text-xs"
                />
                {citySearch.loading && (
                  <RefreshCw className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-gray-400" strokeWidth={1.5} />
                )}
                {cityDropdownOpen && citySearch.results.length > 0 && (
                  <ul className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-brand-border rounded-lg shadow-lg max-h-52 overflow-y-auto text-xs">
                    {citySearch.results.map(([name, regionCode], i) => {
                      const cityLoc = buildCityLocation({
                        countryCode: locCC,
                        countryName: locCountryName,
                        entry: [name, regionCode],
                        allCities: cityCache[locCC] ?? [],
                      });
                      const already = value.locations.some((l) => locKey(l) === locKey(cityLoc));
                      const regionName = resolveRegionName(locCC, regionCode);
                      return (
                        <li
                          key={`${name}-${regionCode}-${i}`}
                          className={cn(
                            'px-3 py-1.5 cursor-pointer hover:bg-brand-primary/5 flex items-center justify-between',
                            already && 'opacity-40',
                          )}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (!already) {
                              addLocation(cityLoc);
                            }
                            citySearch.clear();
                            setCityDropdownOpen(false);
                          }}
                        >
                          <span>
                            <span className="font-medium text-gray-800">{name}</span>
                            {regionName && <span className="text-gray-500 ml-1">({regionName})</span>}
                          </span>
                          {already && <span className="text-[10px] text-gray-500">déjà ajoutée</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {cityDropdownOpen && citySearch.query.length >= 2 && !citySearch.loading && citySearch.results.length === 0 && (
                  <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-brand-border rounded-lg shadow-lg p-2 text-xs text-gray-500">
                    Aucune ville trouvée. Appuyer Entrée pour ajouter &quot;{citySearch.query}&quot; manuellement.
                  </div>
                )}
              </div>

              {selectedCities.length > 0 && (
                <div className="flex flex-wrap gap-1 p-1.5 bg-green-50 border border-green-200 rounded-lg">
                  {selectedCities.map((loc) => {
                    const idx = value.locations.findIndex(
                      (l) => locKey(l) === locKey(loc),
                    );
                    return (
                      <span
                        key={locKey(loc)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-green-200 rounded text-xs text-green-700"
                      >
                        <span className="font-mono text-[10px] opacity-60">{loc.countryCode}</span>
                        {formatLocationLabel(loc)}
                        <button onClick={() => removeLoc(idx)} className="opacity-50 hover:opacity-100 hover:text-red-500">
                          <X className="h-3 w-3" strokeWidth={2} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Region list */}
          {locLevel === 'region' && (
            <div className="border border-brand-border rounded-lg overflow-y-auto max-h-56 bg-white">
              <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-b border-brand-border sticky top-0 z-10">
                <span className="text-[11px] text-gray-500">
                  {selectedInCC.filter((l) => l.kind === 'region').length} / {countryRegions.length}
                </span>
                <button
                  onClick={() => addAllForCountry(locCC, locCountryName, 'region')}
                  className="text-xs text-brand-primary hover:underline"
                >
                  Tout sélectionner
                </button>
              </div>
              {filteredRegions.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500">Aucune région trouvée pour &quot;{locSearch}&quot;</p>
              ) : (
                filteredRegions.map((r) => {
                  const checked = value.locations.some((l) => l.kind === 'region' && l.code === r.code);
                  return (
                    <label
                      key={r.code}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-gray-50',
                        checked && 'bg-brand-primary/5',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRegion(r)}
                        className="w-3 h-3 accent-brand-primary shrink-0"
                      />
                      <span className="truncate">{r.name}</span>
                    </label>
                  );
                })
              )}
            </div>
          )}

          {/* Department list */}
          {locLevel === 'department' && (
            <div className="border border-brand-border rounded-lg overflow-y-auto max-h-56 bg-white">
              <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-b border-brand-border sticky top-0 z-10">
                <span className="text-[11px] text-gray-500">
                  {selectedInCC.filter((l) => l.kind === 'department').length} depts sélectionnés
                </span>
                <button
                  onClick={() => addAllForCountry(locCC, locCountryName, 'department')}
                  className="text-xs text-brand-primary hover:underline"
                >
                  Tout sélectionner le pays
                </button>
              </div>
              {filteredRegionsForDepts.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500">Aucun résultat pour &quot;{locSearch}&quot;</p>
              ) : (
                filteredRegionsForDepts.map((region) => {
                  const allDepts = WORLD_DEPTS_BY_REGION[region.code] ?? [];
                  const q = locSearch.trim().toLowerCase();
                  const depts = q
                    ? allDepts.filter(
                        (d) => d.name.toLowerCase().includes(q) || region.name.toLowerCase().includes(q),
                      )
                    : allDepts;
                  const isExp = expandedRegions.has(region.code) || q.length > 0;
                  const selDepts = allDepts.filter((d) =>
                    value.locations.some((l) => l.kind === 'department' && l.code === d.code),
                  );
                  return (
                    <div key={region.code}>
                      <div
                        className="flex items-center justify-between px-2 py-1 bg-gray-50/60 border-b border-brand-border cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleExpand(region.code)}
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500">{isExp ? '▼' : '▶'}</span>
                          <span className="text-xs font-bold text-gray-800 truncate">{region.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {selDepts.length > 0 && (
                            <span className="text-[11px] text-brand-primary font-bold">
                              {selDepts.length}/{allDepts.length}
                            </span>
                          )}
                          <button
                            className="text-xs text-brand-primary hover:underline"
                            onMouseDown={(e) => { e.stopPropagation(); addAllForRegion(region.code); }}
                          >
                            tout
                          </button>
                          {selDepts.length > 0 && (
                            <button
                              className="text-xs text-red-500 hover:underline"
                              onMouseDown={(e) => { e.stopPropagation(); clearAllForRegion(region.code); }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      {isExp &&
                        depts.map((d) => {
                          const checked = value.locations.some(
                            (l) => l.kind === 'department' && l.code === d.code,
                          );
                          return (
                            <label
                              key={d.code}
                              className={cn(
                                'flex items-center gap-2 px-4 py-0.5 text-xs cursor-pointer hover:bg-gray-50',
                                checked && 'bg-brand-primary/5',
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleDept(d)}
                                className="w-3 h-3 accent-brand-primary shrink-0"
                              />
                              <span className="truncate">{d.name}</span>
                            </label>
                          );
                        })}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Selected location chips */}
          {value.locations.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-1.5 bg-gray-50 rounded-lg border border-brand-border">
              {value.locations.map((loc, i) => (
                <span
                  key={`${loc.countryCode}-${loc.kind}-${loc.code ?? loc.name}-${i}`}
                  className={cn(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 border rounded text-xs',
                    loc.kind === 'department' && 'bg-brand-primary/10 border-brand-primary/30 text-brand-primary',
                    loc.kind === 'region' && 'bg-brand-primary/15 border-brand-primary/40 text-brand-primary',
                    loc.kind === 'city' && 'bg-green-50 border-green-200 text-green-700',
                  )}
                >
                  <span className="font-mono text-[10px] opacity-60">{loc.countryCode}</span>
                  {formatLocationLabel(loc)}
                  <button
                    onClick={() => removeLoc(i)}
                    className="opacity-50 hover:opacity-100 hover:text-red-500"
                    aria-label={`Retirer ${loc.name}`}
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cartesian product summary */}
      {value.industries.length > 0 && value.locations.length > 0 && (
        <div className="p-2 bg-brand-primary/5 border border-brand-primary/30 rounded-lg text-xs text-brand-primary text-center">
          ✓ {value.industries.length} industrie{value.industries.length > 1 ? 's' : ''} ×{' '}
          {value.locations.length} location{value.locations.length > 1 ? 's' : ''} ={' '}
          <strong>{value.industries.length * value.locations.length} jobs</strong> à générer
        </div>
      )}

      {/* ── Selection flags ── */}
      <div className="border-t border-brand-border pt-3 space-y-2" data-testid="selection-flags">
        <span className="text-xs font-bold text-gray-800">Données à collecter</span>
        {(Object.keys(SELECTION_FLAG_LABELS) as (keyof LeadScrapeSelectionFlags)[]).map((key) => (
          <div key={key} className="flex items-center justify-between" data-testid={`flag-${key}`}>
            <span className="text-xs text-gray-700">{SELECTION_FLAG_LABELS[key]}</span>
            <Switch
              checked={value.selectionFlags[key]}
              onChange={(v) => setFlag(key, v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function locKey(l: LeadScrapeLocation): string {
  return l.code
    ? `${l.countryCode}|${l.kind}|${l.code}`
    : `${l.countryCode}|${l.kind}|${l.name.toLowerCase()}`;
}
